export type RouteAccess = 'public' | 'authenticated' | 'client' | 'staff' | 'admin' | 'development' | 'system';
export type RouteShell = 'public' | 'client' | 'portal' | 'admin' | 'standalone';
export type SitemapCategory = 'main' | 'products' | 'demos' | 'tools' | 'content' | 'legal';
export type SitemapChangeFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface RouteSitemapConfig {
  title: string;
  description: string;
  category: SitemapCategory;
  priority: number;
  changeFrequency: SitemapChangeFrequency;
  status?: 'live' | 'demo';
}

export interface AppRouteManifestEntry {
  path: string;
  access: RouteAccess;
  shell: RouteShell;
  indexable: boolean;
  sitemap?: RouteSitemapConfig;
}

type SitemapRouteTuple = readonly [
  path: string,
  title: string,
  description: string,
  category: SitemapCategory,
  priority: number,
  changeFrequency: SitemapChangeFrequency,
  status?: 'live' | 'demo',
];

const sitemapRoute = ([
  path,
  title,
  description,
  category,
  priority,
  changeFrequency,
  status = 'live',
]: SitemapRouteTuple): AppRouteManifestEntry => {
  const indexable = status !== 'demo';

  return {
    path,
    access: 'public',
    shell: 'public',
    indexable,
    sitemap: { title, description, category, priority, changeFrequency, status },
  };
};

const route = (
  path: string,
  access: RouteAccess,
  shell: RouteShell,
  indexable = false,
): AppRouteManifestEntry => ({ path, access, shell, indexable });

const PUBLIC_SITEMAP_ROUTES: SitemapRouteTuple[] = [
  ['/', 'Homepage', 'Dialgia Group AI-powered business solutions', 'main', 1, 'daily'],
  ['/about', 'About Us', 'Learn about Dialgia Group and its mission', 'main', 0.8, 'monthly'],
  ['/welcome', 'Welcome', 'Discover Dialgia Group products and services', 'main', 0.6, 'monthly'],
  ['/products', 'Products', 'Explore the Dialgia Group product catalogue', 'products', 0.9, 'weekly'],
  ['/portfolio', 'Portfolio', 'View Dialgia Group project work', 'main', 0.7, 'monthly'],
  ['/contact', 'Contact Us', 'Contact the Dialgia Group team', 'main', 0.7, 'monthly'],
  ['/pricing', 'Pricing', 'Review Dialgia Group product and service pricing', 'main', 0.8, 'weekly'],
  ['/ai-discovery', 'AI Discovery', 'Discover suitable AI solutions for your business', 'tools', 0.7, 'monthly'],
  ['/ai-agents', 'AI Agents', 'Explore Dialgia Group AI agents', 'products', 0.8, 'weekly'],
  ['/solutions/compare', 'Compare Solutions', 'Compare Dialgia Group solutions', 'products', 0.7, 'weekly'],
  ['/email-banners', 'Email Banner Gallery', 'Browse Dialgia Group email banner designs', 'content', 0.5, 'monthly'],
  ['/faq', 'Frequently Asked Questions', 'Answers to common Dialgia Group questions', 'content', 0.6, 'weekly'],
  ['/blog', 'Business Insights', 'Read Dialgia Group news and business insights', 'content', 0.7, 'daily'],
  ['/checkmysite', 'Check My Site', 'Analyse your website with Dialgia Group', 'tools', 0.7, 'monthly'],
  ['/sitemap', 'Site Map', 'Browse public Dialgia Group pages', 'content', 0.3, 'monthly'],
  ['/careers', 'Careers', 'Explore careers at Dialgia Group', 'main', 0.6, 'weekly'],
  ['/privacy', 'Privacy Policy', 'How Dialgia Group protects personal data', 'legal', 0.3, 'yearly'],
  ['/legal', 'Legal Terms', 'Dialgia Group legal terms and conditions', 'legal', 0.3, 'yearly'],
  ['/cookie-policy', 'Cookie Policy', 'How Dialgia Group uses cookies', 'legal', 0.3, 'yearly'],
  ['/gdpr-compliance', 'GDPR Compliance', 'Dialgia Group GDPR compliance information', 'legal', 0.3, 'yearly'],
  ['/price-beat-guarantee', 'Price Beat Guarantee', 'Dialgia Group competitive pricing commitment', 'legal', 0.4, 'monthly'],
  ['/workforce-management/demo', 'Workforce Management Demo', 'Interactive workforce management demonstration', 'demos', 0.6, 'monthly', 'demo'],
  ['/service-desk/demo', 'IT Service Desk Demo', 'Interactive service desk demonstration', 'demos', 0.6, 'monthly', 'demo'],
  ['/facilities-management/demo', 'Facilities Management Demo', 'Interactive facilities management demonstration', 'demos', 0.6, 'monthly', 'demo'],
  ['/digital-newsroom/demo', 'Digital Newsroom Demo', 'Interactive digital newsroom demonstration', 'demos', 0.6, 'monthly', 'demo'],
  ['/quality-assurance/demo', 'Quality Assurance Demo', 'Interactive quality assurance demonstration', 'demos', 0.6, 'monthly', 'demo'],
  ['/call-recording/demo', 'Call Recording Demo', 'Interactive call recording demonstration', 'demos', 0.6, 'monthly', 'demo'],
  ['/payroll/demo', 'Payroll Demo', 'Interactive payroll system demonstration', 'demos', 0.6, 'monthly', 'demo'],
  ['/eor/demo', 'Employer of Record Demo', 'Interactive employer of record demonstration', 'demos', 0.6, 'monthly', 'demo'],
  ['/centurion-global-protect/demo', 'Centurion Global Protect Demo', 'Interactive security platform demonstration', 'demos', 0.6, 'monthly', 'demo'],
  ['/hr-management/demo', 'HR Management Demo', 'Interactive HR management demonstration', 'demos', 0.6, 'monthly', 'demo'],
  ['/passenger-transportation/demo', 'Passenger Transportation Demo', 'Interactive passenger transportation demonstration', 'demos', 0.6, 'monthly', 'demo'],
  ['/time-attendance/demo', 'Time and Attendance Demo', 'Interactive time and attendance demonstration', 'demos', 0.6, 'monthly', 'demo'],
  ['/products/coworking-management-suite/demo', 'Coworking Management Demo', 'Interactive coworking management demonstration', 'demos', 0.6, 'monthly', 'demo'],
  ['/products/website-webapp-suite/demo', 'Website and Web App Demo', 'Interactive website and web application demonstration', 'demos', 0.6, 'monthly', 'demo'],
  ['/marketing/demo', 'Marketing Suite Demo', 'Interactive marketing suite demonstration', 'demos', 0.6, 'monthly', 'demo'],
];

const PUBLIC_INDEXABLE_ROUTES = [
  '/marketing',
  '/products/category/:categorySlug',
  '/products/marketing-sales-suite',
  '/products/facilities-management-suite',
  '/products/digital-newsroom-platform',
  '/products/quality-assurance-system',
  '/products/call-recording-system',
  '/products/workforce-management-system',
  '/products/it-service-desk-platform',
  '/products/coworking-management-suite',
  '/products/website-webapp-suite',
  '/products/complete-payroll-system',
  '/products/global-employer-of-record',
  '/products/centurion-global-protect',
  '/products/complete-hr-management-platform',
  '/products/passenger-transportation-management',
  '/products/time-attendance-system',
  '/products/:productId',
  '/blog/:slug',
];

const PUBLIC_NO_INDEX_ROUTES = [
  '/homepage-2',
  '/demo-access/:productType',
  '/checkmysite/share/:token',
  '/client-login',
  '/auth',
  '/auth/callback',
  '/forgot-password',
  '/password-reset',
  '/website-wizard',
  '/payment-success',
  '/connect',
];

const AUTHENTICATED_ROUTES = [
  '/profile',
  '/my-profile',
  '/client-profile',
  '/prd/:scopeId',
  '/onboarding',
];

const CLIENT_ROUTES = [
  '/client-portal',
  '/client-hub',
  '/client-workspace',
  '/workspace/:projectId',
];

const STAFF_ROUTES = [
  '/portal',
  '/portal/checklist',
  '/portal/training',
  '/portal/policies',
  '/portal/contracts-nda',
  '/portal/google-workspace',
  '/portal/email-setup',
  '/portal/documents',
  '/portal/support',
  '/portal/welcome-sessions',
  '/portal/connected-tools',
  '/portal/my-day',
  '/portal/learning',
  '/portal/quality',
  '/portal/apps/:integrationKey',
];

const ADMIN_ROUTES = [
  '/admin',
  '/blog/:slug/edit',
  '/security',
  '/user-adoption',
  '/analytics',
  '/icarly-analytics',
  '/deployment',
  '/admin/release-readiness',
  '/admin/incidents',
  '/admin/edge-function-health',
  '/admin/email-preview',
  '/admin/onboarding/templates',
  '/admin/onboarding/users',
  '/admin/onboarding/content',
  '/admin/onboarding/policies',
  '/admin/onboarding/training',
  '/admin/onboarding/integrations',
  '/admin/onboarding/analytics',
  '/admin/onboarding/invitations',
  '/admin/onboarding/contracts',
  '/admin/knowledge-base',
];

const SYSTEM_ROUTES = [
  '/api/merchant-feed.xml',
  '/api/merchant-feed.tsv',
  '/api/merchant-feed.csv',
  '/.lovable/oauth/consent',
];

const DEVELOPMENT_ROUTES = ['/responsive-qa', '/a11y-check'];

export const APP_ROUTE_MANIFEST: AppRouteManifestEntry[] = [
  ...PUBLIC_SITEMAP_ROUTES.map(sitemapRoute),
  ...PUBLIC_INDEXABLE_ROUTES.map(path => route(path, 'public', 'public', true)),
  ...PUBLIC_NO_INDEX_ROUTES.map(path => route(path, 'public', 'standalone')),
  ...AUTHENTICATED_ROUTES.map(path => route(path, 'authenticated', 'client')),
  ...CLIENT_ROUTES.map(path => route(path, 'client', 'client')),
  ...STAFF_ROUTES.map(path => route(path, 'staff', 'portal')),
  ...ADMIN_ROUTES.map(path => route(path, 'admin', 'admin')),
  ...SYSTEM_ROUTES.map(path => route(path, 'system', 'standalone')),
  ...DEVELOPMENT_ROUTES.map(path => route(path, 'development', 'standalone')),
  route('*', 'system', 'standalone'),
];

const normalizePath = (pathname: string) => {
  const path = pathname.split(/[?#]/, 1)[0] || '/';
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
};

const matchesPattern = (pattern: string, pathname: string) => {
  if (pattern === '*') return true;

  const patternSegments = pattern.split('/').filter(Boolean);
  const pathSegments = pathname.split('/').filter(Boolean);
  if (patternSegments.length !== pathSegments.length) return false;

  return patternSegments.every((segment, index) => (
    segment.startsWith(':') || segment === pathSegments[index]
  ));
};

export const getRouteManifestEntry = (pathname: string) => {
  const path = normalizePath(pathname);
  return APP_ROUTE_MANIFEST.find(entry => matchesPattern(entry.path, path));
};

export const isIndexableRoute = (pathname: string) => (
  getRouteManifestEntry(pathname)?.indexable === true
);

export const getSitemapRouteEntries = (): Array<AppRouteManifestEntry & { sitemap: RouteSitemapConfig }> => (
  APP_ROUTE_MANIFEST.filter(
    (entry): entry is AppRouteManifestEntry & { sitemap: RouteSitemapConfig } => (
      entry.indexable && Boolean(entry.sitemap)
    )
  )
);

export const getNoIndexRoutePatterns = () => (
  APP_ROUTE_MANIFEST.filter(entry => !entry.indexable && entry.path !== '*').map(entry => entry.path)
);
